import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { QRCodeSVG } from 'qrcode.react';
import './App.css';

// API Configuration
// Use relative path when running in Docker/production, only use localhost:3000 during React dev (port 3000)
const API_URL = window.location.port === '3000'
  ? 'http://localhost:3000/api'
  : '/api';

const REQUIRED_BILLING_TOKEN = 'GgtUZvEmm92xYwgxseav4bAI9kok1Z0rIKxjbFeVCgcsSQfGj04cBOSfCatLCRIW8lR6ZTdD7FRh6jgs4LYGZzY42ByMGqUmadgv4ZYLl5ekPIr6aqwYcDjikoglCxGjAu2JpiTIfUWeeyvafcfZME2yYTAEBJBrkaiH7iL9oz87otsrzXUZrDc53qhRgFxlcSuWeudMy0KtO3bGafGQtSbZ1YYxxQuyrOGKaoVDMu';
const BILLING_ENABLED = process.env.REACT_APP_BILLING_MODULE_ENABLED === 'true' &&
  process.env.REACT_APP_BILLING_MODULE_TOKEN === REQUIRED_BILLING_TOKEN;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));

  const login = (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ token, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Toast Context
const ToastContext = createContext();

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const useToast = () => useContext(ToastContext);

// Confirm Context
const ConfirmContext = createContext();

const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    message: '',
    title: '',
    resolve: null,
  });

  const showConfirm = useCallback((message, title = 'Bestätigung') => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        message,
        title,
        resolve,
      });
    });
  }, []);

  const handleClose = (result) => {
    if (confirmState.resolve) {
      confirmState.resolve(result);
    }
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <ConfirmContext.Provider value={{ showConfirm }}>
      {children}
      {confirmState.isOpen && (
        <div className="confirm-overlay" onClick={() => handleClose(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmState.title}</h3>
            <p>{confirmState.message}</p>
            <div className="confirm-actions">
              <button
                className="btn"
                onClick={() => handleClose(false)}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleClose(true)}
              >
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

const useConfirm = () => useContext(ConfirmContext);

// Axios request interceptor - add Authorization header
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Axios response interceptor - handle expired tokens and auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If token is expired, invalid, or missing (401 or 403), logout and redirect to login
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const errorMessage = error.response.data?.error || '';
      // Don't redirect on login page itself to avoid infinite loop
      if (!window.location.pathname.includes('/login')) {
        console.warn('Authentication failed, logging out...', errorMessage);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Login Component
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/admin/login`, {
        username,
        password,
      });

      login(response.data.token, response.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Hof Kaiser Admin</h1>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label>Benutzername</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Anmelden...' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-layout">
      <nav className="navbar">
        <div className="navbar-brand">
          <span style={{ fontSize: '28px' }}>🌾</span>
          <h2>Hof Kaiser Verwaltung</h2>
        </div>
        <div className="navbar-menu">
          <Link to="/dashboard" className={location.pathname === '/dashboard' ? 'active' : ''}>Dashboard</Link>
          <Link to="/time-entries" className={location.pathname === '/time-entries' ? 'active' : ''}>Zeiteinträge</Link>
          <Link to="/employees" className={location.pathname === '/employees' ? 'active' : ''}>Mitarbeiter</Link>
          <Link to="/stands" className={location.pathname === '/stands' ? 'active' : ''}>Stände</Link>
          {BILLING_ENABLED && (
            <Link to="/billing" className={location.pathname === '/billing' ? 'active' : ''}>Abrechnungen</Link>
          )}
          <Link to="/auswertung" className={location.pathname === '/auswertung' ? 'active' : ''}>Auswertung</Link>
          <Link to="/export" className={location.pathname === '/export' ? 'active' : ''}>Export</Link>
          <Link to="/settings" className={location.pathname === '/settings' ? 'active' : ''}>Einstellungen</Link>
        </div>
        <div className="navbar-user">
          <span>{user?.username}</span>
          <button onClick={handleLogout} className="btn btn-small">
            Abmelden
          </button>
        </div>
      </nav>
      <main className="main-content">{children}</main>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Lädt...</div>;

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats?.todayEntries || 0}</div>
          <div className="stat-label">Einträge heute</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.currentlyCheckedIn || 0}</div>
          <div className="stat-label">Aktuell eingecheckt</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.activeStands || 0}</div>
          <div className="stat-label">Aktive Stände</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.activeEmployees || 0}</div>
          <div className="stat-label">Aktive Mitarbeiter</div>
        </div>
      </div>
    </div>
  );
};

// Time Entries Component
const TimeEntries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('thisMonth');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [correctionModal, setCorrectionModal] = useState(null);
  const { showToast } = useToast();
  const { showConfirm } = useConfirm();

  const getMonthRange = (offset = 0) => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return {
      start: format(first, 'yyyy-MM-dd'),
      end: format(last, 'yyyy-MM-dd'),
    };
  };

  const getEffectiveDates = () => {
    if (filterMode === 'thisMonth') {
      return getMonthRange(0);
    } else if (filterMode === 'lastMonth') {
      return getMonthRange(-1);
    }
    return { start: startDate, end: endDate };
  };

  useEffect(() => {
    setPage(1);
  }, [filterMode, startDate, endDate]);

  useEffect(() => {
    loadEntries();
  }, [page, filterMode, startDate, endDate]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const { start, end } = getEffectiveDates();
      const params = { page, limit: 50 };
      if (start) params.startDate = start;
      if (end) params.endDate = end;

      const response = await axios.get(`${API_URL}/admin/time-entries`, { params });
      setEntries(response.data.entries);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load entries:', error);
      showToast('Fehler beim Laden der Zeiteinträge', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCorrection = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/admin/time-entries/${correctionModal.id}`, {
        checkIn: correctionModal.checkIn,
        checkOut: correctionModal.checkOut,
        correctionNote: correctionModal.note,
      });
      setCorrectionModal(null);
      loadEntries();
      showToast('Korrektur erfolgreich gespeichert', 'success');
    } catch (error) {
      showToast('Fehler beim Speichern: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleDelete = async (entry) => {
    const confirmed = await showConfirm(
      `Zeiteintrag von ${entry.first_name} ${entry.last_name} am ${format(parseISO(entry.check_in), 'dd.MM.yyyy HH:mm', { locale: de })} wirklich löschen?`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API_URL}/admin/time-entries/${entry.id}`);
      showToast('Zeiteintrag gelöscht', 'success');
      loadEntries();
    } catch (error) {
      showToast('Fehler beim Löschen: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const filteredEntries = entries.filter((entry) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      entry.first_name.toLowerCase().includes(term) ||
      entry.last_name.toLowerCase().includes(term) ||
      entry.personal_number.toLowerCase().includes(term) ||
      entry.stand_name.toLowerCase().includes(term)
    );
  });

  return (
    <div className="time-entries">
      <h1>Zeiteinträge</h1>

      <div className="filters" style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Zeitraum</label>
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
            style={{ minWidth: '180px' }}
          >
            <option value="thisMonth">Dieser Monat</option>
            <option value="lastMonth">Letzter Monat</option>
            <option value="custom">Freier Zeitraum</option>
          </select>
        </div>

        {filterMode === 'custom' && (
          <>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Von</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Bis</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Suche</label>
          <input
            type="text"
            placeholder="Name, Personalnr. oder Stand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '250px' }}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Mitarbeiter</th>
                <th>Stand</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Stunden</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry, index) => {
                const currentDate = format(parseISO(entry.check_in), 'yyyy-MM-dd');
                const prevDate = index > 0 ? format(parseISO(filteredEntries[index - 1].check_in), 'yyyy-MM-dd') : null;
                const showSeparator = prevDate && currentDate !== prevDate;
                return (
                  <React.Fragment key={entry.id}>
                    {showSeparator && (
                      <tr className="date-separator-row">
                        <td colSpan="8"><div className="date-separator-bar"></div></td>
                      </tr>
                    )}
                    <tr>
                  <td>{format(parseISO(entry.check_in), 'dd.MM.yyyy', { locale: de })}</td>
                  <td>{`${entry.first_name} ${entry.last_name} (${entry.personal_number})`}</td>
                  <td>{entry.stand_name}</td>
                  <td>{format(parseISO(entry.check_in), 'HH:mm', { locale: de })}</td>
                  <td>
                    {entry.check_out
                      ? format(parseISO(entry.check_out), 'HH:mm', { locale: de })
                      : '-'}
                  </td>
                  <td>{entry.hours_worked ? `${entry.hours_worked}h` : '-'}</td>
                  <td>
                    {entry.auto_checkout && <span className="badge badge-warning">Auto</span>}
                    {entry.corrected && <span className="badge badge-info">Korrigiert</span>}
                  </td>
                  <td style={{ display: 'flex', gap: '4px' }}>
                    <button
                        onClick={() =>
                          setCorrectionModal({
                            id: entry.id,
                            entry,
                            checkIn: entry.check_in ? entry.check_in.slice(0, 16) : '',
                            checkOut: entry.check_out
                              ? entry.check_out.slice(0, 16)
                              : new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Berlin' }).replace(' ', 'T').slice(0, 16),
                            note: '',
                          })
                        }
                        className="btn btn-small"
                      >
                        Korrigieren
                      </button>
                    <button
                      onClick={() => handleDelete(entry)}
                      className="btn btn-small btn-danger"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {pagination && (
            <div className="pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="btn"
              >
                Vorherige
              </button>
              <span>
                Seite {page} von {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.totalPages}
                className="btn"
              >
                Nächste
              </button>
            </div>
          )}
        </>
      )}

      {correctionModal && (
        <div className="modal-overlay" onClick={() => setCorrectionModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Zeiteintrag korrigieren</h2>
            <form onSubmit={handleCorrection}>
              <div className="form-group">
                <label>Mitarbeiter</label>
                <input
                  type="text"
                  value={`${correctionModal.entry.first_name} ${correctionModal.entry.last_name}`}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Check-in Zeit</label>
                <input
                  type="datetime-local"
                  value={correctionModal.checkIn}
                  onChange={(e) =>
                    setCorrectionModal({ ...correctionModal, checkIn: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Check-out Zeit</label>
                <input
                  type="datetime-local"
                  value={correctionModal.checkOut}
                  onChange={(e) =>
                    setCorrectionModal({ ...correctionModal, checkOut: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Notiz (optional)</label>
                <textarea
                  value={correctionModal.note}
                  onChange={(e) =>
                    setCorrectionModal({ ...correctionModal, note: e.target.value })
                  }
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setCorrectionModal(null)} className="btn">
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Stands Component
const Stands = () => {
  const [stands, setStands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const { showToast } = useToast();
  const { showConfirm } = useConfirm();

  const categorizeStands = (stands) => {
    const categories = [
      { key: 'hofladen', label: 'Hofladen', items: [] },
      { key: 'wochenmarkt', label: 'Wochenmarkt', items: [] },
      { key: 'sonstige', label: 'Stände', items: [] },
    ];
    stands.forEach((stand) => {
      const name = stand.name.toLowerCase();
      if (name.includes('hofladen')) {
        categories[0].items.push(stand);
      } else if (name.includes('wochenmarkt')) {
        categories[1].items.push(stand);
      } else {
        categories[2].items.push(stand);
      }
    });
    return categories.filter((c) => c.items.length > 0);
  };

  const toggleCategory = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    loadStands();
  }, []);

  const loadStands = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/stands`);
      setStands(response.data.stands);
    } catch (error) {
      console.error('Failed to load stands:', error);
      showToast('Fehler beim Laden der Stände', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (modal.id) {
        await axios.put(`${API_URL}/admin/stands/${modal.id}`, modal);
      } else {
        await axios.post(`${API_URL}/admin/stands`, modal);
      }
      setModal(null);
      loadStands();
      showToast('Stand erfolgreich gespeichert', 'success');
    } catch (error) {
      showToast('Fehler beim Speichern: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleToggleActive = async (stand) => {
    try {
      await axios.put(`${API_URL}/admin/stands/${stand.id}`, {
        active: !stand.active
      });
      loadStands();
      showToast(`Stand erfolgreich ${stand.active ? 'deaktiviert' : 'aktiviert'}`, 'success');
    } catch (error) {
      showToast('Fehler beim Aktualisieren: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleHardDelete = async (id) => {
    const confirmed = await showConfirm('Möchten Sie diesen Stand wirklich UNWIDERRUFLICH löschen? Alle zugehörigen Zeiterfassungen gehen verloren!');
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/admin/stands/${id}`);
      loadStands();
      showToast('Stand erfolgreich gelöscht', 'success');
    } catch (error) {
      showToast('Fehler beim Löschen: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await axios.post(`${API_URL}/admin/stands/bulk-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(`Import erfolgreich: ${response.data.message}`, 'success');
      setImportFile(null);
      loadStands();
    } catch (error) {
      showToast('Fehler beim Import: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  return (
    <div className="stands">
      <div className="page-header">
        <h1>Stände verwalten</h1>
        <button onClick={() => setModal({ name: '', description: '', active: true })} className="btn btn-primary">
          Neuer Stand
        </button>
      </div>

      <div className="import-section">
        <h3>CSV Import</h3>
        <form onSubmit={handleImport} className="import-form">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setImportFile(e.target.files[0])}
          />
          <button type="submit" className="btn" disabled={!importFile}>
            Importieren
          </button>
        </form>
        <small>Format: Name, Beschreibung, Aktiv (eine Zeile pro Stand)</small>
        <br />
        <button
          type="button"
          className="btn btn-small"
          style={{ marginTop: '8px', fontSize: '0.85em' }}
          onClick={() => {
            const csv = '\uFEFFName,Beschreibung,Aktiv\nHofladen,Verkaufsstand Hofladen,true\nWochenmarkt,Stand am Wochenmarkt,true';
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'vorlage_staende.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Vorlage herunterladen
        </button>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : (
        categorizeStands(stands).map((category) => (
          <div key={category.key} className="category-section" style={{ marginBottom: '20px' }}>
            <div
              className="category-header"
              onClick={() => toggleCategory(category.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                userSelect: 'none',
              }}
            >
              <span style={{
                display: 'inline-block',
                width: '20px',
                fontWeight: 'bold',
                fontSize: '14px',
                transition: 'transform 0.2s',
                transform: collapsed[category.key] ? 'rotate(-90deg)' : 'rotate(0deg)',
              }}>
                &#9660;
              </span>
              <h2 style={{ margin: 0, fontSize: '18px' }}>
                {category.label}
                <span style={{ marginLeft: '10px', fontSize: '14px', color: '#6c757d', fontWeight: 'normal' }}>
                  ({category.items.length})
                </span>
              </h2>
            </div>
            {!collapsed[category.key] && (
              <table className="data-table" style={{ marginTop: '8px' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Beschreibung</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {category.items.map((stand) => (
                    <tr key={stand.id}>
                      <td>{stand.name}</td>
                      <td>{stand.description || '-'}</td>
                      <td>
                        <span className={`badge ${stand.active ? 'badge-success' : 'badge-secondary'}`}>
                          {stand.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => setModal(stand)} className="btn btn-small">
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleToggleActive(stand)}
                          className={`btn btn-small ${stand.active ? 'btn-secondary' : 'btn-success'}`}
                          style={{ marginLeft: '8px', backgroundColor: stand.active ? '#6c757d' : '#28a745' }}
                        >
                          {stand.active ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                        <button onClick={() => handleHardDelete(stand.id)} className="btn btn-small btn-danger">
                          Löschen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modal.id ? 'Stand bearbeiten' : 'Neuer Stand'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Name*</label>
                <input
                  type="text"
                  value={modal.name}
                  onChange={(e) => setModal({ ...modal, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={modal.description || ''}
                  onChange={(e) => setModal({ ...modal, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={modal.active}
                    onChange={(e) => setModal({ ...modal, active: e.target.checked })}
                  />
                  Aktiv
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setModal(null)} className="btn">
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Employees Component
const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [stands, setStands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [timeModal, setTimeModal] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');
  const { showToast } = useToast();
  const { showConfirm } = useConfirm();

  useEffect(() => {
    loadEmployees();
    loadStands();
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/employees`);
      setEmployees(response.data.employees);
    } catch (error) {
      console.error('Failed to load employees:', error);
      showToast('Fehler beim Laden der Mitarbeiter', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStands = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stands?active=true`);
      setStands(response.data.stands);
    } catch (error) {
      console.error('Failed to load stands:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        personal_number: modal.personal_number,
        first_name: modal.first_name,
        last_name: modal.last_name,
        is_favorite: modal.is_favorite || false,
        active: modal.active,
        stand_ids: [modal.stand_id_1, modal.stand_id_2].filter(Boolean).map(Number),
      };
      if (modal.id) {
        await axios.put(`${API_URL}/admin/employees/${modal.id}`, payload);
      } else {
        await axios.post(`${API_URL}/admin/employees`, payload);
      }
      setModal(null);
      loadEmployees();
      showToast('Mitarbeiter erfolgreich gespeichert', 'success');
    } catch (error) {
      showToast('Fehler beim Speichern: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await axios.put(`${API_URL}/admin/employees/${employee.id}`, {
        active: !employee.active
      });
      loadEmployees();
      showToast(`Mitarbeiter erfolgreich ${employee.active ? 'deaktiviert' : 'aktiviert'}`, 'success');
    } catch (error) {
      showToast('Fehler beim Aktualisieren: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleToggleFavorite = async (employee) => {
    try {
      await axios.put(`${API_URL}/admin/employees/${employee.id}`, {
        is_favorite: !employee.is_favorite
      });
      loadEmployees();
    } catch (error) {
      showToast('Fehler beim Aktualisieren', 'error');
    }
  };

  const handleHardDelete = async (id) => {
    const confirmed = await showConfirm('Möchten Sie diesen Mitarbeiter wirklich UNWIDERRUFLICH löschen? Alle zugehörigen Zeiterfassungen gehen verloren!');
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/admin/employees/${id}`);
      loadEmployees();
      showToast('Mitarbeiter erfolgreich gelöscht', 'success');
    } catch (error) {
      showToast('Fehler beim Löschen: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      // Step 1: Preview - check for unknown stands
      const preview = await axios.post(`${API_URL}/admin/employees/bulk-import-preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { unknownStands, totalEmployees, matchedStands } = preview.data;

      if (unknownStands && unknownStands.length > 0) {
        // Show preview modal with unknown stands
        setImportPreview({ unknownStands, totalEmployees, matchedStands, file: importFile });
      } else {
        // No unknown stands - import directly
        await executeImport(importFile, false);
      }
    } catch (error) {
      showToast('Fehler beim Import: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const executeImport = async (file, createMissingStands) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('createMissingStands', createMissingStands ? 'true' : 'false');

    try {
      const response = await axios.post(`${API_URL}/admin/employees/bulk-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const msg = response.data.message;
      const created = response.data.createdStands || [];
      if (created.length > 0) {
        showToast(`${msg} | ${created.length} neue Stände angelegt: ${created.map(s => s.name).join(', ')}`, 'success');
      } else {
        showToast(`Import erfolgreich: ${msg}`, 'success');
      }
      setImportFile(null);
      setImportPreview(null);
      loadEmployees();
    } catch (error) {
      showToast('Fehler beim Import: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const openEditModal = (employee) => {
    const sa = employee.stand_assignments || [];
    setModal({
      ...employee,
      stand_id_1: sa[0]?.stand_id || '',
      stand_id_2: sa[1]?.stand_id || '',
    });
  };

  const openTimeModal = (employee) => {
    const sa = employee.stand_assignments || [];
    setTimeModal({
      employee_id: employee.id,
      employee_name: `${employee.last_name}, ${employee.first_name}`,
      stand_id: sa[0]?.stand_id || '',
      check_in: '',
      check_out: '',
      correction_note: '',
    });
  };

  const handleTimeEntry = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/admin/time-entries`, {
        employee_id: timeModal.employee_id,
        stand_id: parseInt(timeModal.stand_id),
        check_in: timeModal.check_in,
        check_out: timeModal.check_out,
        correction_note: timeModal.correction_note || 'Manuell nachgetragen',
      });
      setTimeModal(null);
      showToast(response.data.message, 'success');
    } catch (error) {
      showToast('Fehler: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const filteredEmployees = employees.filter((e) => {
    if (filterFavorite && !e.is_favorite) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        e.last_name.toLowerCase().includes(term) ||
        e.first_name.toLowerCase().includes(term) ||
        e.personal_number.toLowerCase().includes(term)
      );
    }
    return true;
  }).sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'personal_number':
        return dir * a.personal_number.localeCompare(b.personal_number, 'de', { numeric: true });
      case 'last_name':
        return dir * a.last_name.localeCompare(b.last_name, 'de');
      case 'first_name':
        return dir * a.first_name.localeCompare(b.first_name, 'de');
      case 'stand':
        const standA = (a.stand_assignments || []).map(s => s.stand_name).join(', ');
        const standB = (b.stand_assignments || []).map(s => s.stand_name).join(', ');
        return dir * standA.localeCompare(standB, 'de');
      case 'favorite':
        return dir * ((a.is_favorite ? 1 : 0) - (b.is_favorite ? 1 : 0));
      case 'status':
        return dir * ((a.active ? 1 : 0) - (b.active ? 1 : 0));
      default:
        return 0;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortArrow = ({ field }) => (
    <span style={{ marginLeft: '4px', opacity: sortField === field ? 1 : 0.3, fontSize: '12px' }}>
      {sortField === field && sortDir === 'desc' ? '\u25BC' : '\u25B2'}
    </span>
  );

  return (
    <div className="employees">
      <div className="page-header">
        <h1>Mitarbeiter verwalten</h1>
        <button onClick={() => setModal({ personal_number: '', first_name: '', last_name: '', is_favorite: false, active: true, stand_id_1: '', stand_id_2: '' })} className="btn btn-primary">
          Neuer Mitarbeiter
        </button>
      </div>

      <div className="import-section">
        <h3>Mitarbeiter Import (CSV / DATEV)</h3>
        <form onSubmit={handleImport} className="import-form">
          <input
            type="file"
            accept=".csv,.tsv,.txt"
            onChange={(e) => setImportFile(e.target.files[0])}
          />
          <button type="submit" className="btn" disabled={!importFile}>
            Importieren
          </button>
        </form>
        <small>CSV-Format: Personalnummer, Nachname, Vorname, Favorit, Aktiv, Stand 1, Stand 2</small>
        <br />
        <small>DATEV-Format: Tab-getrennt mit Helfer Nr., Name, Vorname, Ordner (Titelzeile wird automatisch übersprungen)</small>
        <br />
        <button
          type="button"
          className="btn btn-small"
          style={{ marginTop: '8px', fontSize: '0.85em' }}
          onClick={() => {
            const csvContent = '\uFEFFPersonalnummer,Nachname,Vorname,Favorit,Aktiv,Stand 1,Stand 2\n1001,Müller,Max,false,true,Hofladen Salem,WM Lauenburg\n1002,Schmidt,Anna,false,true,Citti Kiel,';
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'vorlage_mitarbeiter.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Vorlage herunterladen
        </button>
      </div>

      {importPreview && (
        <div className="modal-overlay" onClick={() => setImportPreview(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <h2>Import-Vorschau</h2>
            <p><strong>{importPreview.totalEmployees}</strong> Mitarbeiter erkannt</p>

            {importPreview.matchedStands.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Zugeordnete Stände ({importPreview.matchedStands.length}):</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '0.9em', color: '#2e7d32' }}>
                  {importPreview.matchedStands.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </div>
            )}

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ff9800' }}>
              <strong>Unbekannte Stände ({importPreview.unknownStands.length}):</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {importPreview.unknownStands.map((s) => (
                  <li key={s} style={{ fontWeight: 'bold' }}>{s}</li>
                ))}
              </ul>
              <p style={{ margin: '8px 0 0', fontSize: '0.9em' }}>
                Diese Stände existieren noch nicht in der Datenbank. Sollen sie automatisch angelegt werden?
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn"
                style={{ backgroundColor: '#757575' }}
                onClick={() => setImportPreview(null)}
              >
                Abbrechen
              </button>
              <button
                className="btn"
                onClick={() => executeImport(importPreview.file, false)}
              >
                Ohne neue Stände importieren
              </button>
              <button
                className="btn btn-primary"
                onClick={() => executeImport(importPreview.file, true)}
              >
                Stände anlegen & importieren
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="filters" style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <input
            type="text"
            placeholder="Suche nach Name oder Personalnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '280px' }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={filterFavorite}
            onChange={(e) => setFilterFavorite(e.target.checked)}
          />
          Nur Favoriten anzeigen
        </label>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('personal_number')} style={{ cursor: 'pointer', userSelect: 'none' }}>Personalnummer <SortArrow field="personal_number" /></th>
              <th onClick={() => handleSort('last_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Nachname <SortArrow field="last_name" /></th>
              <th onClick={() => handleSort('first_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Vorname <SortArrow field="first_name" /></th>
              <th onClick={() => handleSort('stand')} style={{ cursor: 'pointer', userSelect: 'none' }}>Stand(e) <SortArrow field="stand" /></th>
              <th onClick={() => handleSort('favorite')} style={{ cursor: 'pointer', userSelect: 'none', width: '30px' }}>Favorit <SortArrow field="favorite" /></th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>Status <SortArrow field="status" /></th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((employee) => (
              <tr key={employee.id}>
                <td>{employee.personal_number}</td>
                <td>{employee.last_name}</td>
                <td>{employee.first_name}</td>
                <td>
                  {(employee.stand_assignments || []).length > 0
                    ? (employee.stand_assignments || []).map((sa, i) => (
                        <div key={i}>{sa.stand_name}</div>
                      ))
                    : '-'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span
                    onClick={() => handleToggleFavorite(employee)}
                    style={{ cursor: 'pointer', fontSize: '18px', color: employee.is_favorite ? '#f5a623' : '#ccc' }}
                    title={employee.is_favorite ? 'Favorit entfernen' : 'Als Favorit markieren'}
                  >
                    {employee.is_favorite ? '\u2605' : '\u2606'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${employee.active ? 'badge-success' : 'badge-secondary'}`}>
                    {employee.active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', minWidth: '180px' }}>
                    <button onClick={() => openTimeModal(employee)} className="btn btn-small" style={{ backgroundColor: '#17a2b8', color: 'white' }}>
                      Zeit buchen
                    </button>
                    <button onClick={() => openEditModal(employee)} className="btn btn-small">
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleToggleActive(employee)}
                      className={`btn btn-small ${employee.active ? 'btn-secondary' : 'btn-success'}`}
                      style={{ backgroundColor: employee.active ? '#6c757d' : '#28a745' }}
                    >
                      {employee.active ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    <button onClick={() => handleHardDelete(employee.id)} className="btn btn-small btn-danger">
                      Löschen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modal.id ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Nachname*</label>
                <input
                  type="text"
                  value={modal.last_name}
                  onChange={(e) => setModal({ ...modal, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Vorname*</label>
                <input
                  type="text"
                  value={modal.first_name}
                  onChange={(e) => setModal({ ...modal, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Personalnummer*</label>
                <input
                  type="text"
                  value={modal.personal_number}
                  onChange={(e) => setModal({ ...modal, personal_number: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Stand 1</label>
                <select
                  value={modal.stand_id_1 || ''}
                  onChange={(e) => setModal({ ...modal, stand_id_1: e.target.value })}
                >
                  <option value="">-- Kein Stand --</option>
                  {stands.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Stand 2</label>
                <select
                  value={modal.stand_id_2 || ''}
                  onChange={(e) => setModal({ ...modal, stand_id_2: e.target.value })}
                >
                  <option value="">-- Kein Stand --</option>
                  {stands.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={modal.is_favorite || false}
                    onChange={(e) => setModal({ ...modal, is_favorite: e.target.checked })}
                  />
                  Favorit (Sternchen)
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={modal.active}
                    onChange={(e) => setModal({ ...modal, active: e.target.checked })}
                  />
                  Aktiv
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setModal(null)} className="btn">
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {timeModal && (
        <div className="modal-overlay" onClick={() => setTimeModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Zeit manuell buchen</h2>
            <form onSubmit={handleTimeEntry}>
              <div className="form-group">
                <label>Mitarbeiter</label>
                <input type="text" value={timeModal.employee_name} disabled />
              </div>
              <div className="form-group">
                <label>Stand*</label>
                <select
                  value={timeModal.stand_id}
                  onChange={(e) => setTimeModal({ ...timeModal, stand_id: e.target.value })}
                  required
                >
                  <option value="">-- Stand auswählen --</option>
                  {stands.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Check-in*</label>
                <input
                  type="datetime-local"
                  value={timeModal.check_in}
                  onChange={(e) => setTimeModal({ ...timeModal, check_in: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Check-out*</label>
                <input
                  type="datetime-local"
                  value={timeModal.check_out}
                  onChange={(e) => setTimeModal({ ...timeModal, check_out: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Notiz (optional)</label>
                <textarea
                  value={timeModal.correction_note}
                  onChange={(e) => setTimeModal({ ...timeModal, correction_note: e.target.value })}
                  rows="2"
                  placeholder="Manuell nachgetragen"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setTimeModal(null)} className="btn">
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">
                  Zeit buchen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Auswertung Component
const Auswertung = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [correctionModal, setCorrectionModal] = useState(null);
  const { showToast } = useToast();

  const getDateRange = () => {
    const today = new Date();
    if (filterMode === 'today') {
      const d = format(today, 'yyyy-MM-dd');
      return { start: d, end: d };
    } else if (filterMode === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const d = format(yesterday, 'yyyy-MM-dd');
      return { start: d, end: d };
    }
    return { start: startDate, end: endDate };
  };

  useEffect(() => {
    loadData();
  }, [filterMode, startDate, endDate]);

  const loadData = async () => {
    const { start, end } = getDateRange();
    if (!start || !end) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/reports/missing-checkouts`, {
        params: { startDate: start, endDate: end },
      });
      setEntries(response.data.entries);
    } catch (error) {
      console.error('Failed to load report:', error);
      showToast('Fehler beim Laden der Auswertung', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCorrection = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/admin/time-entries/${correctionModal.id}`, {
        checkIn: correctionModal.checkIn,
        checkOut: correctionModal.checkOut,
        correctionNote: correctionModal.note,
      });
      setCorrectionModal(null);
      loadData();
      showToast('Korrektur erfolgreich gespeichert', 'success');
    } catch (error) {
      showToast('Fehler beim Speichern: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const formatCheckIn = (checkIn) => {
    try {
      return format(parseISO(checkIn), 'dd.MM.yyyy HH:mm', { locale: de });
    } catch {
      return '-';
    }
  };

  const formatTime = (dt) => {
    if (!dt) return null;
    try {
      return format(parseISO(dt), 'HH:mm', { locale: de });
    } catch {
      return '-';
    }
  };

  return (
    <div className="auswertung">
      <h1>Auswertung</h1>

      {/* Missing Checkouts Section */}
      <div className="report-section">
        <div className="report-section-header">
          <h2>Fehlende Checkouts</h2>
          <span className="report-section-subtitle">Mitarbeiter ohne Logout vor 21:00 Uhr</span>
        </div>

        <div className="filters" style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Zeitraum</label>
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              style={{ minWidth: '180px' }}
            >
              <option value="today">Heute</option>
              <option value="yesterday">Gestern</option>
              <option value="custom">Freier Zeitraum</option>
            </select>
          </div>

          {filterMode === 'custom' && (
            <>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Von</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Bis</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {loading ? (
          <div className="loading">Lädt...</div>
        ) : entries.length === 0 ? (
          <div className="report-empty">
            <span className="report-empty-icon">&#10003;</span>
            <p>Alle Mitarbeiter haben sich ordnungsgemäß ausgecheckt.</p>
          </div>
        ) : (
          <>
            <div className="report-summary-bar">
              <span className="report-count">{entries.length} {entries.length === 1 ? 'Eintrag' : 'Einträge'}</span>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Mitarbeiter</th>
                  <th>Stand</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{format(parseISO(entry.check_in), 'dd.MM.yyyy', { locale: de })}</td>
                    <td>{`${entry.first_name} ${entry.last_name}`}</td>
                    <td>{entry.stand_name}</td>
                    <td>{formatTime(entry.check_in)}</td>
                    <td>
                      {entry.check_out ? (
                        <span className="badge badge-warning">{formatTime(entry.check_out)}</span>
                      ) : (
                        <span className="badge badge-danger">Kein Logout</span>
                      )}
                    </td>
                    <td>
                      {entry.auto_checkout && <span className="badge badge-warning">Auto-Checkout</span>}
                      {entry.corrected && <span className="badge badge-info">Korrigiert</span>}
                      {!entry.check_out && !entry.auto_checkout && !entry.corrected && (
                        <span className="badge badge-danger">Offen</span>
                      )}
                    </td>
                    <td>
                      <button
                          onClick={() =>
                            setCorrectionModal({
                              id: entry.id,
                              entry,
                              checkIn: entry.check_in ? entry.check_in.slice(0, 16) : '',
                              checkOut: entry.check_out
                                ? entry.check_out.slice(0, 16)
                                : new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Berlin' }).replace(' ', 'T').slice(0, 16),
                              note: '',
                            })
                          }
                          className="btn btn-small"
                        >
                          Korrigieren
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {correctionModal && (
        <div className="modal-overlay" onClick={() => setCorrectionModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Zeiteintrag korrigieren</h2>
            <form onSubmit={handleCorrection}>
              <div className="form-group">
                <label>Mitarbeiter</label>
                <input
                  type="text"
                  value={`${correctionModal.entry.first_name} ${correctionModal.entry.last_name}`}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Check-in Zeit</label>
                <input
                  type="datetime-local"
                  value={correctionModal.checkIn}
                  onChange={(e) =>
                    setCorrectionModal({ ...correctionModal, checkIn: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Check-out Zeit</label>
                <input
                  type="datetime-local"
                  value={correctionModal.checkOut}
                  onChange={(e) =>
                    setCorrectionModal({ ...correctionModal, checkOut: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Notiz (optional)</label>
                <textarea
                  value={correctionModal.note}
                  onChange={(e) =>
                    setCorrectionModal({ ...correctionModal, note: e.target.value })
                  }
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setCorrectionModal(null)} className="btn">
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Export Component
const Export = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const handleExport = async (format) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await axios.get(`${API_URL}/admin/export/${format}?${params}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zeiterfassung_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Export erfolgreich gestartet', 'success');
    } catch (error) {
      showToast('Export fehlgeschlagen: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="export">
      <h1>Daten exportieren</h1>

      <div className="export-card">
        <div className="form-group">
          <label>Von Datum</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Bis Datum</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="export-buttons">
          <button
            onClick={() => handleExport('csv')}
            disabled={loading}
            className="btn btn-primary"
          >
            Als CSV exportieren
          </button>
          <button
            onClick={() => handleExport('excel')}
            disabled={loading}
            className="btn btn-primary"
          >
            Als Excel exportieren
          </button>
        </div>
      </div>
    </div>
  );
};

// Billing Component
// Individual Billing Report Item (Accordion)
const BillingReportItem = ({ report, hourlyRate, formatDate, calculateSalary, isLast }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`accordion-item ${expanded ? 'expanded' : ''}`}>
      <div className="accordion-header" onClick={() => setExpanded(!expanded)}>
        <h3>{report.employee.firstName} {report.employee.lastName} ({report.employee.personalNumber})</h3>
        <div className="header-stats">
          <span>{report.summary.totalHours.toFixed(2)}h</span>
          <span className="accordion-icon">▼</span>
        </div>
      </div>
      <div className="accordion-content" style={{ display: expanded ? 'block' : 'none' }}>
        <div className="billing-header">
          <p>
            {formatDate(report.period.startDate)} - {formatDate(report.period.endDate)}
          </p>
        </div>

        {/* Summary cards */}
        <div className="billing-summary-cards">
          <div className="stat-card">
            <div className="stat-value">{report.summary.totalHours.toFixed(2)}h</div>
            <div className="stat-label">Gesamtstunden</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{report.summary.totalDays}</div>
            <div className="stat-label">Arbeitstage</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{report.standBreakdown.length}</div>
            <div className="stat-label">Stände</div>
          </div>
        </div>

        {/* Salary info (if hourly rate provided) */}
        {hourlyRate && (
          <div className="salary-calculator">
            <h3>Gehaltsberechnung</h3>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
              Gesamtgehalt: {calculateSalary(report.summary.totalHours)}
            </div>
          </div>
        )}

        {/* Stand breakdown */}
        <div className="billing-breakdown">
          <h3>Aufschlüsselung nach Ständen</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Stand</th>
                <th>Stunden</th>
                <th>Einträge</th>
                <th>Prozent</th>
              </tr>
            </thead>
            <tbody>
              {report.standBreakdown.map(stand => (
                <tr key={stand.standId}>
                  <td>{stand.standName}</td>
                  <td>{stand.hours.toFixed(2)}h</td>
                  <td>{stand.entryCount}</td>
                  <td>{((stand.hours / report.summary.totalHours) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Daily entries */}
        <div className="billing-details-table">
          <h3>Tägliche Übersicht</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Stand</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Stunden</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {report.dailyEntries.map((entry, index) => (
                <tr key={index}>
                  <td>{format(parseISO(entry.workDate), 'dd.MM.yyyy')}</td>
                  <td>{entry.standName}</td>
                  <td>{format(parseISO(entry.checkIn), 'HH:mm')}</td>
                  <td>{format(parseISO(entry.checkOut), 'HH:mm')}</td>
                  <td>{entry.hoursWorked.toFixed(2)}h</td>
                  <td>
                    {entry.corrected && <span className="badge badge-info">Korrigiert</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {!isLast && <div className="page-break" style={{ pageBreakAfter: 'always' }}></div>}
    </div>
  );
};

// Billing Component
const Billing = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [periodType, setPeriodType] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [billingReports, setBillingReports] = useState([]);
  const [hourlyRate, setHourlyRate] = useState('');
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadEmployees();
    const now = new Date();
    setSelectedMonth(format(now, 'yyyy-MM'));
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/employees`);
      setEmployees(response.data.employees.filter(e => e.active));
    } catch (error) {
      console.error('Failed to load employees:', error);
      showToast('Fehler beim Laden der Mitarbeiter', 'error');
    }
  };

  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: format(date, 'yyyy-MM'),
        label: format(date, 'MMMM yyyy', { locale: de })
      });
    }
    return months;
  };

  const loadBillingData = async () => {
    if (!selectedEmployeeId) {
      showToast('Bitte Mitarbeiter auswählen', 'warning');
      return;
    }

    let startDate, endDate;
    if (periodType === 'month') {
      if (!selectedMonth) {
        showToast('Bitte Monat auswählen', 'warning');
        return;
      }
      const monthDate = new Date(selectedMonth + '-01');
      startDate = format(monthDate, 'yyyy-MM-dd');
      endDate = format(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0), 'yyyy-MM-dd');
    } else {
      if (!customStartDate || !customEndDate) {
        showToast('Bitte Start- und Enddatum auswählen', 'warning');
        return;
      }
      startDate = customStartDate;
      endDate = customEndDate;
    }

    setLoading(true);
    try {
      const endpoint = selectedEmployeeId === 'all'
        ? `${API_URL}/admin/billing/all`
        : `${API_URL}/admin/billing/${selectedEmployeeId}`;

      const response = await axios.get(endpoint, {
        params: { startDate, endDate }
      });

      const data = Array.isArray(response.data) ? response.data : [response.data];
      setBillingReports(data);

      if (data.length === 0) {
        showToast('Keine Daten für diesen Zeitraum gefunden', 'info');
      }
    } catch (error) {
      showToast('Fehler beim Laden: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateSalary = (totalHours) => {
    if (!hourlyRate) return null;
    const rate = parseFloat(hourlyRate);
    if (isNaN(rate)) return null;
    const salary = totalHours * rate;
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(salary);
  };

  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'dd.MM.yyyy', { locale: de });
  };

  const generateExcel = () => {
    const workbook = XLSX.utils.book_new();

    billingReports.forEach(report => {
      const emp = report.employee;
      const sheetName = `${emp.lastName}_${emp.id}`.substring(0, 31);

      const summary = [
        ['Hof Kaiser - Abrechnung'],
        [''],
        ['Mitarbeiter', `${emp.firstName} ${emp.lastName}`],
        ['Personalnummer', emp.personalNumber],
        ['Zeitraum', `${formatDate(report.period.startDate)} - ${formatDate(report.period.endDate)}`],
        [''],
        ['Gesamtstunden', report.summary.totalHours.toFixed(2)],
        ['Stundenlohn', hourlyRate || '-'],
        ['Gesamtgehalt', hourlyRate ? (report.summary.totalHours * parseFloat(hourlyRate)).toFixed(2) : '-'],
        [''],
        ['Stand Aufschlüsselung'],
        ['Stand', 'Stunden', 'Einträge'],
        ...report.standBreakdown.map(s => [s.standName, s.hours.toFixed(2), s.entryCount]),
        [''],
        ['Tägliche Übersicht'],
        ['Datum', 'Stand', 'Check-in', 'Check-out', 'Stunden'],
        ...report.dailyEntries.map(e => [
          format(parseISO(e.workDate), 'dd.MM.yyyy'),
          e.standName,
          format(parseISO(e.checkIn), 'HH:mm'),
          format(parseISO(e.checkOut), 'HH:mm'),
          e.hoursWorked.toFixed(2)
        ])
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(summary);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const filename = billingReports.length === 1
      ? `abrechnung_${billingReports[0].employee.personalNumber}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      : `abrechnungen_gesamt_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="billing-container">
      <h1>Abrechnungen</h1>

      <div className="billing-filters">
        <div className="form-group">
          <label>Mitarbeiter</label>
          <select
            value={selectedEmployeeId}
            onChange={(e) => {
              setSelectedEmployeeId(e.target.value);
              setBillingReports([]);
            }}
          >
            <option value="">Bitte auswählen...</option>
            <option value="all">Alle Mitarbeiter</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name} ({emp.personal_number})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Zeitraum</label>
          <div style={{ display: 'flex', gap: '16px' }}>
            <label>
              <input
                type="radio"
                value="month"
                checked={periodType === 'month'}
                onChange={(e) => setPeriodType(e.target.value)}
              />
              Monat
            </label>
            <label>
              <input
                type="radio"
                value="custom"
                checked={periodType === 'custom'}
                onChange={(e) => setPeriodType(e.target.value)}
              />
              Freier Zeitraum
            </label>
          </div>
        </div>

        {periodType === 'month' ? (
          <div className="form-group">
            <label>Monat</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {generateMonthOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div className="form-group">
              <label>Von</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Bis</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="form-group" style={{ maxWidth: '300px' }}>
          <label>Stundenlohn (€) - optional</label>
          <input
            type="number"
            step="0.01"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            placeholder="z.B. 12.50"
          />
        </div>

        <button
          onClick={loadBillingData}
          disabled={loading}
          className="btn btn-primary"
          style={{ marginTop: '24px' }}
        >
          {loading ? 'Lädt...' : 'Abrechnung laden'}
        </button>
      </div>

      {billingReports.length > 0 && (
        <div className="billing-results">
          {billingReports.map((report, index) => (
            <BillingReportItem
              key={report.employee.id}
              report={report}
              hourlyRate={hourlyRate}
              formatDate={formatDate}
              calculateSalary={calculateSalary}
              isLast={index === billingReports.length - 1}
            />
          ))}

          <div className="export-actions">
            <button onClick={generateExcel} className="btn btn-primary">
              Excel herunterladen
            </button>
            <button onClick={handlePrint} className="btn">
              Bericht drucken / PDF speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Component - API Key Management
const Settings = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [newKeyModal, setNewKeyModal] = useState(null);
  const [keyName, setKeyName] = useState('');
  const { showToast } = useToast();
  const { showConfirm } = useConfirm();
  const [stands, setStands] = useState([]);
  const [standsLoading, setStandsLoading] = useState(true);
  const [printStandId, setPrintStandId] = useState('all');

  useEffect(() => {
    loadApiKeys();
    loadStands();
  }, []);

  const loadStands = async () => {
    setStandsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/stands?active=true`);
      setStands(response.data.stands);
    } catch (error) {
      console.error('Failed to load stands for QR codes:', error);
    } finally {
      setStandsLoading(false);
    }
  };

  const handlePrintQR = (standId) => {
    setPrintStandId(standId);
    // Use setTimeout to ensure state is updated before printing
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/api-keys`);
      setApiKeys(response.data.keys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      showToast('Fehler beim Laden der API-Schlüssel', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!keyName.trim()) {
      showToast('Bitte geben Sie einen Namen ein', 'warning');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/admin/api-keys`, {
        name: keyName.trim()
      });

      // Show the full key in a modal (ONLY SHOWN ONCE)
      setNewKeyModal({
        name: response.data.name,
        key: response.data.key,
        keyPrefix: response.data.keyPrefix
      });

      setCreateModal(false);
      setKeyName('');
      loadApiKeys();
      showToast('API-Schlüssel erfolgreich erstellt', 'success');
    } catch (error) {
      showToast('Fehler beim Erstellen: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleRevokeKey = async (id, name) => {
    const confirmed = await showConfirm(
      `Möchten Sie den API-Schlüssel "${name}" wirklich widerrufen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      'API-Schlüssel widerrufen'
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/admin/api-keys/${id}`);
      loadApiKeys();
      showToast('API-Schlüssel erfolgreich widerrufen', 'success');
    } catch (error) {
      showToast('Fehler beim Widerrufen: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('In Zwischenablage kopiert', 'success');
  };

  return (
    <div className="settings">
      <div className="page-header no-print">
        <h1>Einstellungen</h1>
      </div>

      <div className="settings-section no-print">
        <div className="section-header">
          <h2>API-Schlüssel Verwaltung</h2>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => handlePrintQR('all')} className="btn">
              Alle QR-Codes drucken
            </button>
            <button onClick={() => setCreateModal(true)} className="btn btn-primary">
              Neuer API-Schlüssel
            </button>
          </div>
        </div>

        <p className="section-description">
          API-Schlüssel ermöglichen externen Systemen (wie n8n oder Zapier) den Zugriff auf die API.
          Im Gegensatz zu JWT-Tokens laufen API-Schlüssel nicht ab.
        </p>

        {loading ? (
          <div className="loading">Lädt...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Schlüssel-Präfix</th>
                <th>Erstellt am</th>
                <th>Zuletzt verwendet</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                    Keine API-Schlüssel vorhanden. Erstellen Sie einen neuen Schlüssel.
                  </td>
                </tr>
              ) : (
                apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td><code>{key.keyPrefix}...</code></td>
                    <td>{format(parseISO(key.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}</td>
                    <td>
                      {key.lastUsedAt
                        ? format(parseISO(key.lastUsedAt), 'dd.MM.yyyy HH:mm', { locale: de })
                        : 'Nie'}
                    </td>
                    <td>
                      <span className={`badge ${key.active ? 'badge-success' : 'badge-secondary'}`}>
                        {key.active ? 'Aktiv' : 'Widerrufen'}
                      </span>
                    </td>
                    <td>
                      {key.active && (
                        <button
                          onClick={() => handleRevokeKey(key.id, key.name)}
                          className="btn btn-small btn-danger"
                        >
                          Widerrufen
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Create API Key Modal */}
      {createModal && (
        <div className="modal-overlay" onClick={() => setCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Neuen API-Schlüssel erstellen</h2>
            <form onSubmit={handleCreateKey}>
              <div className="form-group">
                <label>Name*</label>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="z.B. n8n Integration"
                  required
                  autoFocus
                />
                <small>Geben Sie einen beschreibenden Namen ein, um den Zweck dieses Schlüssels zu identifizieren.</small>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setCreateModal(false)} className="btn">
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Display New Key Modal (ONLY SHOWN ONCE) */}
      {newKeyModal && (
        <div className="modal-overlay" onClick={() => setNewKeyModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>API-Schlüssel erfolgreich erstellt</h2>

            <div className="alert alert-warning" style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '16px',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <strong>⚠️ Wichtig:</strong> Dieser Schlüssel wird nur einmal angezeigt.
              Kopieren Sie ihn jetzt und speichern Sie ihn sicher.
              Sie können ihn später nicht erneut abrufen.
            </div>

            <div className="form-group">
              <label>Name</label>
              <input type="text" value={newKeyModal.name} disabled />
            </div>

            <div className="form-group">
              <label>API-Schlüssel</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newKeyModal.key}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '14px' }}
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(newKeyModal.key)}
                  className="btn btn-small"
                >
                  Kopieren
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Verwendung</label>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Verwenden Sie diesen Schlüssel in n8n oder anderen Integrationen,
                indem Sie ihn als <code>X-API-Key</code> Header übergeben:
              </p>
              <pre style={{
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '13px'
              }}>
                X-API-Key: {newKeyModal.key}
              </pre>
            </div>

            <div className="modal-actions">
              <button onClick={() => setNewKeyModal(null)} className="btn btn-primary">
                Verstanden, Schlüssel kopiert
              </button>
            </div>
          </div>
        </div>
      )}

      <hr className="no-print" style={{ margin: '40px 0' }} />

      <div className="settings-section">
        <h2 className="no-print">QR-Codes für Stände</h2>
        <p className="no-print">Drucken Sie QR-Codes aus, die Mitarbeiter scannen können, um sich direkt an einem Stand einzuchecken.</p>

        {standsLoading ? (
          <div className="loading">Lade Stände...</div>
        ) : (
          <div className="qr-grid no-print">
            {stands.map(stand => (
              <div key={stand.id} className="qr-card">
                <h3>{stand.name}</h3>
                <div className="qr-code-wrapper">
                  <QRCodeSVG
                    value={`${process.env.REACT_APP_BASE_URL || 'https://zeiterfassung.home-ki.eu'}/?stand=${stand.id}`}
                    size={160}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <button
                  onClick={() => handlePrintQR(stand.id)}
                  className="btn btn-small"
                  style={{ marginTop: '10px' }}
                >
                  Drucken
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Print-only section - scales to fit any paper size */}
        <div className="qr-print-only">
          {stands
            .filter(stand => printStandId === 'all' || stand.id === printStandId)
            .map(stand => (
              <div key={stand.id} className="qr-page-break">
                <div className="qr-print-content">
                  <h1 className="qr-print-title">{stand.name}</h1>
                  <div className="qr-print-code">
                    <QRCodeSVG
                      value={`${process.env.REACT_APP_BASE_URL || 'https://zeiterfassung.home-ki.eu'}/?stand=${stand.id}`}
                      size={550}
                      level="H"
                      style={{ width: '100%', height: 'auto', maxWidth: '60vw', maxHeight: '50vh' }}
                    />
                  </div>
                  <p className="qr-print-label">
                    QR-Code scannen zum{'\n'}Einstempeln/Austempeln
                  </p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" />;
};

// Main App
function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Routes>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/time-entries" element={<TimeEntries />} />
                        <Route path="/employees" element={<Employees />} />
                        <Route path="/stands" element={<Stands />} />
                        {BILLING_ENABLED ? (
                          <Route path="/billing" element={<Billing />} />
                        ) : (
                          <Route path="/billing" element={<Navigate to="/dashboard" />} />
                        )}
                        <Route path="/auswertung" element={<Auswertung />} />
                        <Route path="/export" element={<Export />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/" element={<Navigate to="/dashboard" />} />
                      </Routes>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
